import type { Action, MetaAction } from '~/actions'
import type { CartUpsell, Checkout, LineItem, StagedUpsell } from '~/types'
import type { GQLError } from '~/utils'

interface _State {
	open: boolean
	errors: GQLError[] | null
	pendingOperation: GID | 'cart' | null
	stagedUpsells: StagedUpsell[]
	upsells: CartUpsell[]
}

export type UnloadedCart = _State &
	Partial<Checkout> & {
		loaded: false
	}

export type LoadedCart = _State &
	Checkout & {
		loaded: true
	}

export type CartState = UnloadedCart | LoadedCart

export const initialState: CartState = {
	open: false,
	loaded: false,
	errors: null,
	pendingOperation: null,
	stagedUpsells: [],
	upsells: []
}

function item(state: LineItem, action: MetaAction): LineItem {
	if (action.meta != state.id) return state

	switch (action.type) {
		case 'CART_QUANTITY_UPDATE':
			return {
				...state,
				quantity: action.payload
			}

		default:
			return state
	}
}

export default function (state: CartState = initialState, action: Action): CartState {
	if (action.type == 'CART_LOAD_SUCCESS')
		return {
			...state,
			...action.payload,
			loaded: true,
			errors: null,
			open: false,
			// Only set to false if it's not from localStorage
			pendingOperation:
				(action.payload as CartState).pendingOperation !== undefined ? null : state.pendingOperation
		}
	else if (!state.loaded) return state

	switch (action.type) {
		case 'CART_OPEN':
			return {
				...state,
				open: action.payload
			}

		case 'CART_ADD':
			return {
				...state,
				errors: null,
				pendingOperation: action.payload[0].id
			}

		case 'CART_QUANTITY_UPDATE':
			return {
				...state,
				errors: null,
				pendingOperation: action.meta,
				lines: state.lines.map(it => item(it, action))
			}

		case 'CART_REMOVE':
			return {
				...state,
				errors: null,
				lines: state.lines.filter(it => it.id != action.meta),
				pendingOperation: action.meta
			}

		case 'CART_COUNTRY_UPDATE_SUCCESS':
		case 'CART_UPDATE_SUCCESS':
			return {
				...state,
				...action.payload,
				errors: null,
				pendingOperation: null
			}

		case 'CART_UPDATE_ERROR':
			return {
				...state,
				...action.payload, // Revert to original cart state
				open: true,
				pendingOperation: null,
				errors: action.meta
			}

		case 'CART_UPSELLS_LOAD_SUCCESS':
			return {
				...state,
				upsells: action.payload
			}

		case 'UPSELL_ADD':
			return {
				...state,
				stagedUpsells: [...state.stagedUpsells, action.payload]
			}

		case 'UPSELL_UPDATE':
			return {
				...state,
				stagedUpsells: state.stagedUpsells.map(it => (it.id == action.meta ? { ...it, ...action.payload } : it))
			}

		case 'UPSELL_REMOVE':
			return {
				...state,
				stagedUpsells: state.stagedUpsells.filter(it => it.id != action.meta)
			}
	}

	return state
}
